import dayjs from "dayjs";
import { LloFunnelReportItem } from "../../../../api";
import type { FilterFieldKey, LloFunnelFiltersState } from "./types";

function normalizeMatch(value?: string | null) {
  return (value ?? "").trim().toLowerCase();
}

function matchesExact(value: string | null | undefined, filter: string) {
  if (!filter) return true;
  return normalizeMatch(value) === normalizeMatch(filter);
}

function matchesDueDateRange(
  dueDate: string | null | undefined,
  from: LloFunnelFiltersState["dueDateFrom"],
  to: LloFunnelFiltersState["dueDateTo"]
) {
  if (!from && !to) return true;
  if (!dueDate) return false;

  const date = dayjs(dueDate);
  if (!date.isValid()) return false;
  if (from && date.isBefore(from, "day")) return false;
  if (to && date.isAfter(to, "day")) return false;
  return true;
}

export function applyLloFunnelFilters(
  rows: LloFunnelReportItem[],
  filters: LloFunnelFiltersState
): LloFunnelReportItem[] {
  return rows.filter(
    (row) =>
      matchesExact(row.caseName, filters.caseName) &&
      matchesExact(row.raa, filters.raa) &&
      matchesExact(row.ap, filters.ap) &&
      matchesExact(row.lloExpiration, filters.lloExpiration) &&
      matchesExact(row.negotiation, filters.negotiation) &&
      matchesExact(row.funnel, filters.funnel) &&
      matchesExact(row.taskName, filters.taskName) &&
      matchesExact(row.taskAssignee, filters.taskAssignee) &&
      matchesDueDateRange(row.dueDate, filters.dueDateFrom, filters.dueDateTo)
  );
}

export function uniqueFilterOptions(
  rows: LloFunnelReportItem[],
  field: FilterFieldKey
): string[] {
  const values = new Set<string>();
  rows.forEach((row) => {
    const value = (row[field] ?? "").trim();
    if (value) values.add(value);
  });
  return [...values].sort((a, b) => a.localeCompare(b));
}

export function countActiveFilters(filters: LloFunnelFiltersState): number {
  let count = 0;
  (Object.keys(filters) as (keyof LloFunnelFiltersState)[]).forEach((key) => {
    const value = filters[key];
    if (key === "dueDateFrom" || key === "dueDateTo") {
      if (value != null) count += 1;
      return;
    }
    if (typeof value === "string" && value.trim()) count += 1;
  });
  return count;
}

export function hasActiveFilters(filters: LloFunnelFiltersState): boolean {
  return countActiveFilters(filters) > 0;
}
